import api from '../api/client';
import type { RentalReportData } from '../../types/rentalReport';

export type BookingActionErrorInfo = {
  message: string;
  code?: string;
  maxEndAt: string | null;
};

/** Läser fel-svar från boknings-API (inkl. maxEndAt vid VEHICLE_TIME_TAKEN). */
export function parseBookingActionError(err: unknown): BookingActionErrorInfo {
  const data = (err as {
    response?: {
      data?: {
        error?: string;
        detail?: string;
        code?: string;
        details?: { maxEndAt?: unknown };
      };
    };
  })?.response?.data;
  const raw = data?.details?.maxEndAt;
  return {
    message: (typeof data?.detail === 'string' ? data.detail : data?.error) || '',
    code: data?.code,
    maxEndAt: typeof raw === 'string' && raw.trim() !== '' ? raw : null,
  };
}

export function isBookingChangeLocked(b: {
  importLocked?: boolean;
  poolBookingsLocked?: boolean;
}): boolean {
  return Boolean(b.importLocked || b.poolBookingsLocked);
}

/** GeoJSON Polygon för geofence-område */
export type GeofenceGeometry = { type: 'Polygon'; coordinates: number[][][] } | null;

export interface PickupLocationClosure {
  id: string;
  pickupLocationId: string;
  closedFrom: string;
  closedTo: string | null;
  reason: string | null;
  createdAt: string;
}

export interface PickupLocationCabinet {
  id: string;
  cabinet_id: string;
  cabinet_name: string | null;
  pickup_location_id: string | null;
  pickup_location_name?: string;
}

export interface PickupLocationAvailableCabinet {
  cabinet_id: string;
  cabinet_name: string | null;
  organization_id: string;
}

export interface PickupLocation {
  id: string;
  organizationId: string;
  name: string;
  description: string | null;
  bookingInfo?: string | null;
  /** Visa bokarens namn på bokningsblock i resurshanteraren */
  showBookerNameInResourceManager?: boolean;
  address: string | null;
  postalCode: string | null;
  city: string | null;
  isActive: boolean;
  /** Poolen syns men bokningar kan inte skapas/ändras förrän låset släpps. */
  bookingsLocked?: boolean;
  isVehiclePool?: boolean;
  displayOrder: number;
  geofenceGeometry: GeofenceGeometry;
  /** true = bilen måste vara inom geofence-området vid incheckning/avslut via app. */
  geofenceEnforceOnReturn?: boolean;
  /** Max avstånd (m) till annan pools parkeringsyta vid sökning av lediga bilar. */
  nearbyPoolSearchRadiusM?: number;
  /** true = Tidslinje är standardvy i Kundtjänst för poolens toppnodsorganisation. */
  bookingDefaultTimelineView?: boolean;
  allowExternalBooking?: boolean;
  defaultSupplierId?: string | null;
  /** Visas/bokningsbar tidigast detta datum (YYYY-MM-DD). null = ingen begränsning. */
  validFrom?: string | null;
  /** Visas/bokningsbar senast detta datum (YYYY-MM-DD). null = ingen begränsning. */
  validTo?: string | null;
  /** NULL = ärv från org. 0 = regeln gäller inte. >0 = egna minuter för no-show frigör. */
  noShowReleaseMinutesOverride?: number | null;
  bookingNoBeforeOverride?: string | null;
  bookingNoAfterOverride?: string | null;
  bookingMinLengthHoursOverride?: number | null;
  bookingMaxLengthHoursOverride?: number | null;
  bookingRestMinutesOverride?: number | null;
  bookingMinLeadTimeHoursOverride?: number | null;
  fallbackSupplierIds?: string[];
  /** Biluthyrarens org som hanterar poolen (rental_management_links). NULL = egen pool. */
  managedBySupplierOrgId?: string | null;
  /** Överenskomna bilklasser från biluthyrarlänken (vid request-bokning). */
  allowedCarClasses?: string[];
  bookingCount?: number;
  /** Antal fordon mappade till utlämningsplatsen */
  vehicleCount?: number;
  /** Meter – sök lediga fordon även i närliggande pooler (kräver kartområde). null/0 = endast egen pool. */
  nearbyPoolSearchRadiusMeters?: number | null;
  /** Ansvariga personer för platsen */
  responsibleUsers?: PickupLocationResponsibleUser[];
  createdAt: string;
  updatedAt: string;
}

export function pickupLocationOptionLabel(pl: PickupLocation, vehiclePoolSuffix: string): string {
  return pl.isVehiclePool ? `${pl.name} (${vehiclePoolSuffix})` : pl.name;
}

export interface PickupLocationResponsibleUser {
  userId: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  displayOrder: number;
}

/** Skrivpayload till API (ansvariga skickas som userId-lista, inte responsibleUsers). */
export type PickupLocationWritePayload = Partial<PickupLocation> & {
  responsibleUserIds?: string[];
};

export type SupplierType = 'CAR_RENTAL' | 'OTHER';

export interface Supplier {
  id: string;
  organizationId: string;
  organizationName?: string | null;
  name: string;
  type: SupplierType;
  contactInfo: Record<string, unknown> | null;
  carClasses?: string[];
  isActive: boolean;
  customerId: string | null;
  customerName: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface BookingStats {
  range: { from: string; to: string };
  summary: {
    totalBookings: number;
    byStatus: Record<string, number>;
    totalRequests: number;
    totalAssigned: number;
    requestSharePct: number;
    uniqueBookers: number;
    avgLengthHours: number;
    totalEstimatedKm: number;
    /** Antal bokningar som automatiskt frigjorts pga no-show (status NO_SHOW). */
    noShowCount: number;
    /** Antal servicebokningar totalt i perioden. */
    serviceTotalCount: number;
    /** Servicebokningar som aldrig hämtades ut/användes. */
    serviceNotUsedCount: number;
    /** Andel oanvända servicebokningar av alla servicebokningar (%). */
    serviceNotUsedPct: number;
  };
  occupancy: {
    basis: 'calendar' | 'workdays';
    bookedHours: number;
    capacityHours: number;
    occupancyPct: number;
    vehicleCount: number;
  };
  byCarClass: { carClass: string; count: number }[];
  topVehicles: { vehicleId: number | null; registrationNumber: string | null; count: number }[];
  trend: { period: string; count: number; bookedHours: number }[];
}

export type RentalAccessLevel = 'FULL_BOOKING' | 'STATS_ONLY';

export interface RentalManagementLink {
  id: string;
  biluthyrareOrganizationId: string;
  biluthyrareOrganizationName: string | null;
  biluthyrareCrmCustomerId: string | null;
  biluthyrareCrmCustomerName: string | null;
  customerOrganizationId: string;
  customerOrganizationName: string | null;
  accessLevel: RentalAccessLevel;
  allowedCarClasses: string[];
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface Destination {
  id: string;
  organizationId: string;
  name: string;
  address: string | null;
  postalCode: string | null;
  city: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CostCenter {
  id: string;
  organizationId: string;
  code: string;
  description: string;
  validFrom: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface BookingProject {
  id: number;
  organizationId: string;
  code: string;
  description: string;
  accountString: string | null;
  validFrom: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export type BookingFormFieldSection = 'booking' | 'group' | 'vehicle' | 'trip' | 'admin';

export interface EffectiveBookingFormField {
  code: string;
  section: BookingFormFieldSection;
  isVisible: boolean;
  isRequired: boolean;
  locked: boolean;
  groupOnly?: boolean;
  isListOnly: boolean;
  supportsListOnly: boolean;
  forcedListOnly: boolean;
}

export interface BookingTripPurpose {
  id: number;
  organizationId: string;
  name: string;
  displayOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ProjectImportResult {
  created: number;
  updated: number;
  errors: { row: number; message: string }[];
}

/** Bokningskostnader per resurstyp */
export interface ResourceTypeCostPayload {
  baseCostPerBooking?: number | null;
  hourlyCost?: number | null;
  kmCost?: number | null;
  cancellationFee?: number | null;
  cancellationHours?: number | null;
  maxDailyCost?: number | null;
  monthlyCost30d?: number | null;
  billingTimeFrom?: string | null;
  billingTimeTo?: string | null;
  offHoursHourlyCost?: number | null;
}

export interface ResourceTypeCostRow extends ResourceTypeCostPayload {
  resourceTypeId: string;
  resourceTypeCode: string;
  resourceTypeName: string;
  isCustomerType: boolean;
}

export interface PickupResourceTypeCostOverride extends ResourceTypeCostPayload {
  resourceTypeId: string;
  resourceTypeCode: string;
  resourceTypeName: string;
}

/** Resurstyp på systemnivå (Bil, Cykel, Konferensrum, etc.) */
export interface ResourceType {
  id: string;
  code: string;
  name: string;
  nameEn?: string | null;
  displayOrder: number;
  isActive: boolean;
  organizationId?: string | null;
  resourceCount?: number;
  createdAt: string;
  updatedAt: string;
}

/** Kundens egen resurstyp (organization_id satt) */
export type OrgResourceType = ResourceType & { organizationId: string; resourceCount: number };

export interface BookingResource {
  id: string;
  organizationId: string;
  resourceTypeId: string;
  resourceTypeName: string;
  resourceTypeCode: string;
  identity: string;
  name: string;
  description: string | null;
  isActive: boolean;
  pickupLocationId: string | null;
  pickupLocationName: string | null;
  pickupLocationVehicleId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PickupLocationVehicle {
  id: string;
  pickupLocationId: string;
  pickupLocationName?: string | null;
  fromNearbyPool?: boolean;
  vehicleId: number | null;
  bookingResourceId?: string | null;
  bookingResourceIdentity?: string | null;
  bookingResourceName?: string | null;
  resourceTypeId?: string | null;
  resourceTypeName?: string | null;
  resourceTypeCode?: string | null;
  /** Fordonets organisation; används för att undvika falsk «annan org» när bilen bara saknas i max 500-rader */
  vehicleOrganizationId?: string | null;
  supplierId: string | null;
  externalVehicleRef: string | null;
  displayName: string | null;
  isAvailable: boolean;
  registrationNumber?: string;
  brand?: string | null;
  model?: string | null;
  supplierName?: string;
  fuelType?: string | null;
  year?: number | null;
  initialOdometer?: number | null;
  fuelTankCapacity?: number | null;
  avgFuelConsumption?: number | null;
  estimatedRangeKm?: number | null;
  seats?: number | null;
  transmission?: string | null;
  bookingDescription?: string | null;
  equipmentTags?: string[];
  allocationType?: 'OWN' | 'PERMANENT' | 'BOOKING_ONLY' | null;
}

export type FleetVehicleOption = {
  vehicleId: number;
  registrationNumber: string;
  brand: string | null;
  model: string | null;
  fuelType: string | null;
};

export type BookingBasisListItem = {
  id: string;
  startAt: string;
  endAt: string;
  checkedOutAt: string | null;
  returnedAt: string | null;
  status: string;
  customerName: string | null;
  bookedByUserName: string | null;
  organizationName: string | null;
  pickupLocationName: string | null;
  registrationNumber: string | null;
  vehicleBrand: string | null;
  vehicleModel: string | null;
  allocationType: string | null;
  vehicleId: number | null;
};

export type BookingBasisHeader = BookingBasisListItem & {
  bookedByUserId: string | null;
  pickupLocationId: string | null;
  notes: string | null;
};

export type BookingBasisPayload = {
  booking: BookingBasisHeader;
  report: RentalReportData;
};

export interface EquipmentTag {
  id: string;
  organizationId: string;
  organizationName?: string | null;
  name: string;
  icon: string | null;
  isActive: boolean;
  displayOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface VehicleEquipmentItem {
  id: string;
  vehicleId: number;
  equipmentTagId: string;
  tagName: string;
  tagIcon: string | null;
  notes: string | null;
  createdAt: string;
}

export interface BookingPurpose {
  id: string;
  organizationId: string;
  name: string;
  isActive: boolean;
  displayOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface BookingTerms {
  id: string;
  organizationId: string;
  version: string;
  title: string;
  content: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface BookingMember {
  id: string;
  bookingId: string;
  userId: string;
  role: string;
  canCheckout: boolean;
  checkedOutAt: string | null;
  returnedAt: string | null;
  checkoutSource: string | null;
  returnSource: string | null;
  createdAt: string;
  userName?: string;
  userEmail?: string;
  keywinPinCode?: string | null;
}

export interface VehiclePoolCandidateKey {
  id: string;
  name: string;
  vehicleId: number;
  registrationNumber: string | null;
  cabinetId: string | null;
}

export interface VehiclePoolAssignment {
  id: string;
  organizationId?: string;
  pickupLocationId: string;
  pickupLocationName?: string;
  name: string;
  maxHoursPerCheckout: number;
  isActive: boolean;
  keywinBasketId?: string | null;
  selectedKeyIds?: string[] | null;
  cabinetId?: string | null;
  cabinetName?: string | null;
  createdAt: string;
  updatedAt: string;
  memberCount?: number;
  activeCheckoutCount?: number;
}

export interface VehiclePoolMember {
  id: string;
  poolAssignmentId: string;
  userId: string;
  validFrom: string | null;
  validTo: string | null;
  isActive: boolean;
  keywinPinCode?: string | null;
  createdAt: string;
  userName?: string;
  userEmail?: string;
}

export type BookingType = 'SINGLE' | 'GROUP' | 'POOL';
export type BookingStatus = 'BOOKED' | 'CHECKED_OUT' | 'RETURNED' | 'CANCELLED' | 'CANCELLED_BY_SYSTEM' | 'NO_SHOW';

/** Administrativ blockering (grått i kalender). */
export interface VehicleBookingBlock {
  id: string;
  vehicleId: number;
  registrationNumber: string | null;
  startAt: string;
  endAt: string;
}

export interface OrgVehicleBookingBlock extends VehicleBookingBlock {
  vehicleLabel: string | null;
  damageReportId: number | null;
  damageDescription: string | null;
  damageStatus: string | null;
  bookingId: string | null;
}

export type VehicleBlockHours = 12 | 24 | 120;
export type DamageTypeCode = 'collision' | 'theft' | 'fire' | 'vandalism' | 'other';

export interface BlockResourceResult {
  id: string;
  damageReportId: number;
  reassigned: number;
  collisions: number;
  sourceBookingClosed?: boolean;
}

export interface CollisionBookingRow {
  id: string;
  customerName: string | null;
  startAt: string;
  endAt: string;
  status: BookingStatus;
  pickupLocationId: string;
  pickupLocationName: string;
  priorPickupLocationVehicleId: string | null;
  priorVehicleId: number | null;
  priorRegistrationNumber: string | null;
  priorPickupLocationId: string | null;
  memberCount?: number | null;
  memberNames?: string | null;
}

export interface BookingRequestRow {
  id: string;
  customerName: string | null;
  startAt: string;
  endAt: string;
  status: BookingStatus;
  pickupLocationId: string;
  pickupLocationName: string;
  organizationId?: string | null;
  organizationName?: string | null;
  supplierNames: string | null;
  suggestedRegistrationNumber: string | null;
  requestedCarClass: string | null;
  memberCount?: number | null;
  memberNames?: string | null;
}

export interface Booking {
  id: string;
  pickupLocationVehicleId: string | null;
  pickupLocationId?: string | null;
  /** Användaren som bokningen är gjord för (booked_by_user_id). Används för att avgöra "min bokning". */
  bookedByUserId?: string | null;
  /** Namn på den bokningen gäller för (booked_by_user_id). */
  bookedByUserName?: string | null;
  /** Antal personer på bokningen (gruppbokning inkl. ägare). */
  memberCount?: number | null;
  /** Kommaseparerade namn på alla personer på bokningen (ägare först). */
  memberNames?: string | null;
  /** Vem som faktiskt skapade bokningen (kan skilja vid boka-åt-andra). */
  createdByUserId?: string | null;
  createdByUserName?: string | null;
  priorPickupLocationVehicleId?: string | null;
  priorVehicleId?: number | null;
  priorRegistrationNumber?: string | null;
  priorPickupLocationId?: string | null;
  startAt: string;
  endAt: string;
  customerName: string | null;
  notes: string | null;
  bookingType: BookingType;
  tripPurpose: string | null;
  tripPurposeId: number | null;
  purposeId: string | null;
  purposeName: string | null;
  passengerCount: number;
  termsAcceptedAt: string | null;
  termsVersion: string | null;
  checkedOutAt: string | null;
  returnedAt: string | null;
  checkoutSource: string | null;
  returnSource: string | null;
  needsPurpose: boolean;
  poolAssignmentId: string | null;
  estimatedKm: number | null;
  destinationId: string | null;
  destinationText: string | null;
  costCenterId: string | null;
  costCenterText: string | null;
  projectId: number | null;
  projectText: string | null;
  driverLicenseConfirmed: boolean | null;
  isServiceBooking?: boolean | null;
  approvedByUserId: string | null;
  availableVehicleIdsAtBooking: string[] | null;
  suggestedPickupLocationVehicleId: string | null;
  requestedCarClass?: string | null;
  status: BookingStatus;
  /** Importerad och inte kopplad till person — avbokning/flytt spärras. */
  importLocked?: boolean;
  /** Poolen är synlig men låst — inga ändringar förrän låset släpps. */
  poolBookingsLocked?: boolean;
  /** Referens från old-booking-JSON. Finns kvar även efter koppling till person. */
  oldBookingRef?: string | null;
  keywinGroupBookingId?: string | null;
  createdAt: string;
  updatedAt: string;
  /** Fylls i vid GET enstaka bokning (personal) — avbokningslänk till kund. */
  cancelToken?: string | null;
  pickupLocationName?: string | null;
  /** Fordon-id om bokningen har tilldelat fordon (GET enstaka bokning). */
  vehicleId?: number | null;
  registrationNumber?: string | null;
  /** Kund-/undergrupp-organisationen som poolen tillhör (för förfrågningar). */
  organizationId?: string | null;
  organizationName?: string | null;
}

/** Position för bokningens fordon (GET /api/booking/bookings/:id/vehicle-position). */
export interface BookingVehiclePosition {
  bookingId: string;
  vehicleId: number | null;
  registrationNumber: string | null;
  vehicleLabel: string | null;
  lat: number | null;
  lng: number | null;
  timestamp: string | null;
  speedKmh: number | null;
  heading: number | null;
  source: 'realtime' | 'stored' | null;
}

/** Rad från GET /api/booking/all-bookings (söklista) */
export interface BookingSearchRow {
  id: string;
  customerName: string | null;
  startAt: string;
  endAt: string;
  status: string;
  importLocked?: boolean;
  poolBookingsLocked?: boolean;
  oldBookingRef?: string | null;
  bookingType: string;
  tripPurpose: string | null;
  purposeName: string | null;
  passengerCount: number;
  checkedOutAt: string | null;
  returnedAt: string | null;
  needsPurpose: boolean;
  notes: string | null;
  pickupLocationName: string;
  pickupLocationId: string;
  vehicleLabel: string;
  registrationNumber: string | null;
  /** Fordon-id om bokningen har tilldelat fordon (för t.ex. tankkorts-PIN). */
  vehicleId: number | null;
  resourceTypeName: string | null;
  resourceTypeCode: string | null;
  bookedByUserName: string | null;
  memberCount?: number | null;
  memberNames?: string | null;
  /** Trippar på fordonet som överlappar bokningens period; null utan kopplat fordon. */
  tripCountDuringBooking: number | null;
  createdAt: string;
}

/** Bokningsoptimering – nuläge per pool. */
export interface DayUtilizationPool {
  poolId: string;
  poolName: string;
  totalVehicles: number;
  usedVehicles: number;
  bookingsCount: number;
  utilizationPct: number;
}
export interface DayUtilizationVehicle {
  poolId: string;
  vehicleId: number;
  label: string;
}
export interface DayUtilizationBooking {
  poolId: string;
  bookingId: string;
  vehicleId: number | null;
  label: string;
  startAt: string;
  endAt: string;
  customerName: string | null;
}
export interface DayUtilizationResult {
  date: string;
  pools: DayUtilizationPool[];
  totals: { totalVehicles: number; usedVehicles: number; bookingsCount: number; utilizationPct: number };
  vehicles: DayUtilizationVehicle[];
  bookings: DayUtilizationBooking[];
}
export interface OptimizationSuggestion {
  bookingId: string;
  fromVehicle: string | null;
  toVehicle: string | null;
  reason: string;
  startAt?: string;
  endAt?: string;
}
export interface OptimizeDayResult {
  date: string;
  before: { usedVehicles: number; totalVehicles: number; utilizationPct: number };
  after: { usedVehicles: number; totalVehicles: number; utilizationPct: number };
  freedVehicles: number;
  suggestions: OptimizationSuggestion[];
  summary: string;
  proposedBookings?: DayUtilizationBooking[];
}

export interface FitRequirements {
  resourceTypeId?: string | null;
  automaticOnly?: boolean;
  equipmentTagIds?: string[];
}
export interface FitMove {
  bookingId: string;
  fromPlvId: string;
  fromLabel: string;
  toPlvId: string;
  toLabel: string;
}
export interface FitPlan {
  feasible: boolean;
  reason: string | null;
  moves: FitMove[];
  newBookingPlvId: string | null;
  newBookingLabel: string | null;
  solver: 'cp-sat' | 'local-fallback';
}
export interface FitApplyResult {
  createdBookingId: string;
  moves: (FitMove & { startAt: string; endAt: string })[];
  newBookingPlvId: string;
}

/** Tankkort för ett fordon (behörighetsstyrt – innehåller PIN). */
export interface BookingFuelCard {
  cardNumber: string;
  embossedNumber?: string | null;
  cardName: string | null;
  pinCode: string | null;
  fuelCompanyName: string | null;
}

export interface UserBookingConflict {
  id: string;
  startAt: string;
  endAt: string;
  customerName: string | null;
  status: string;
  pickupLocationName: string | null;
  vehicleLabel: string | null;
}

export type BookingSearchFilters = {
  status?: string;
  dateFrom?: string;
  dateTo?: string;
  pickupLocationId?: string;
  bookingId?: string;
  bookedForSearch?: string;
  vehicleSearch?: string;
  resourceTypeId?: string;
  search?: string;
};

export const bookingApi = {
  async getPickupLocations(
    includeInactive = false,
    ownOrgOnly = false,
    includeVehiclePools = false
  ): Promise<PickupLocation[]> {
    const params: Record<string, string> = {};
    if (includeInactive) params.includeInactive = '1';
    // ownOrgOnly tvingar org-scoping även för global admin (profil/förvald utlämningsplats).
    if (ownOrgOnly) params.ownOrgOnly = '1';
    if (includeVehiclePools) params.includeVehiclePools = '1';
    const res = await api.get('/api/booking/pickup-locations', {
      params: Object.keys(params).length ? params : undefined,
    });
    return res.data.data;
  },
  async createPickupLocation(data: PickupLocationWritePayload & { name: string; organizationId?: string }): Promise<string> {
    const res = await api.post('/api/booking/pickup-locations', data);
    return res.data.data.id;
  },
  async updatePickupLocation(id: string, data: PickupLocationWritePayload): Promise<{ strippedFutureBookings?: number }> {
    const res = await api.put(`/api/booking/pickup-locations/${id}`, data);
    return (res.data?.data ?? {}) as { strippedFutureBookings?: number };
  },
  async getPickupLocationClosures(pickupLocationId: string): Promise<PickupLocationClosure[]> {
    const res = await api.get(`/api/booking/pickup-locations/${pickupLocationId}/closures`);
    return res.data.data;
  },
  async createPickupLocationClosure(
    pickupLocationId: string,
    data: { closedFrom: string; closedTo?: string | null; reason?: string | null }
  ): Promise<{ id: string; strippedFutureBookings: number }> {
    const res = await api.post(`/api/booking/pickup-locations/${pickupLocationId}/closures`, data);
    return res.data.data;
  },
  async deletePickupLocationClosure(pickupLocationId: string, closureId: string): Promise<void> {
    await api.delete(`/api/booking/pickup-locations/${pickupLocationId}/closures/${closureId}`);
  },
  async replaceVehicleInPool(oldPickupLocationVehicleId: string, newPickupLocationVehicleId: string): Promise<{ updatedBookings: number }> {
    const res = await api.post('/api/booking/vehicle-replacement', { oldPickupLocationVehicleId, newPickupLocationVehicleId });
    return res.data.data;
  },
  async takeVehicleOutOfService(plvId: string, reason?: string): Promise<{ reassigned: number; collisions: number }> {
    const res = await api.post(`/api/booking/pickup-location-vehicles/${plvId}/take-out-of-service`, { reason });
    return res.data.data;
  },
  async returnVehicleToService(plvId: string): Promise<void> {
    await api.post(`/api/booking/pickup-location-vehicles/${plvId}/return-to-service`, {});
  },
  async countFutureBookingsForPlv(plvId: string): Promise<number> {
    const res = await api.get(`/api/booking/pickup-location-vehicles/${plvId}/future-bookings-count`);
    return res.data.data.count;
  },
  async deletePickupLocation(id: string): Promise<void> {
    await api.delete(`/api/booking/pickup-locations/${id}`);
  },
  async downloadPickupPoolsExport(): Promise<void> {
    const res = await api.get('/api/booking/pickup-locations/export-pools', { responseType: 'blob' });
    const disposition = res.headers['content-disposition'] as string | undefined;
    const match = disposition?.match(/filename="?([^";]+)"?/i);
    const filename = match?.[1] ?? `utlamningsplatser-pooler-${new Date().toISOString().slice(0, 10)}.xlsx`;
    const url = URL.createObjectURL(res.data as Blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  },

  async getSuppliers(): Promise<Supplier[]> {
    const res = await api.get('/api/booking/suppliers');
    return res.data.data;
  },
  async createSupplier(data: {
    name?: string;
    customerId?: string | null;
    type?: SupplierType;
    contactInfo?: Record<string, unknown>;
    carClasses?: string[];
    organizationId?: string;
  }): Promise<string> {
    const res = await api.post('/api/booking/suppliers', data);
    return res.data.data.id;
  },
  async updateSupplier(id: string, data: Partial<Supplier>): Promise<void> {
    await api.put(`/api/booking/suppliers/${id}`, data);
  },

  // Bokningsstatistik (rapport REPORTS_BOOKING_STATS)
  async getBookingStats(params: {
    from: string;
    to: string;
    organizationId?: string;
    pickupLocationId?: string;
    granularity?: 'day' | 'week' | 'month';
    basis?: 'calendar' | 'workdays';
  }): Promise<BookingStats> {
    const res = await api.get('/api/reports/booking-stats', { params });
    return res.data.data;
  },

  // Biluthyrarkopplingar (Biluthyrare <-> kund)
  async getRentalLinks(): Promise<RentalManagementLink[]> {
    const res = await api.get('/api/booking/rental-links');
    return res.data.data;
  },
  async createRentalLink(data: {
    biluthyrareCrmCustomerId?: string | null;
    biluthyrareOrganizationId?: string | null;
    customerOrganizationId: string;
    accessLevel?: RentalAccessLevel;
    allowedCarClasses?: string[];
  }): Promise<string> {
    const res = await api.post('/api/booking/rental-links', data);
    return res.data.data.id;
  },
  async updateRentalLink(
    id: string,
    data: { accessLevel?: RentalAccessLevel; allowedCarClasses?: string[]; isActive?: boolean }
  ): Promise<void> {
    await api.put(`/api/booking/rental-links/${id}`, data);
  },
  async deleteRentalLink(id: string): Promise<void> {
    await api.delete(`/api/booking/rental-links/${id}`);
  },

  async getDestinations(organizationId?: string): Promise<Destination[]> {
    const params = organizationId ? { organizationId } : {};
    const res = await api.get('/api/booking/destinations', { params });
    return res.data.data;
  },
  async createDestination(data: Partial<Destination> & { name: string; organizationId?: string }): Promise<string> {
    const res = await api.post('/api/booking/destinations', data);
    return res.data.data.id;
  },
  async updateDestination(id: string, data: Partial<Destination>): Promise<void> {
    await api.put(`/api/booking/destinations/${id}`, data);
  },

  async getCostCenters(listAll?: boolean, organizationId?: string): Promise<CostCenter[]> {
    const res = await api.get('/api/booking/cost-centers', {
      params: {
        ...(listAll ? { listAll: 'true' } : {}),
        ...(organizationId ? { organizationId } : {}),
      },
    });
    return res.data.data;
  },
  async createCostCenter(data: {
    code: string;
    description: string;
    validFrom: string;
    organizationId?: string;
  }): Promise<string> {
    const res = await api.post('/api/booking/cost-centers', data);
    return res.data.data.id;
  },
  async updateCostCenter(id: string, data: Partial<{ code: string; description: string; validFrom: string; isActive: boolean }>): Promise<void> {
    await api.put(`/api/booking/cost-centers/${id}`, data);
  },
  async getUserDefaultCostCenter(userId: string): Promise<string | null> {
    const res = await api.get('/api/booking/user-default-cost-center', { params: { userId } });
    return res.data.data ?? null;
  },

  async getFormFieldSettings(params?: { organizationId?: string; pickupLocationOrgId?: string }): Promise<EffectiveBookingFormField[]> {
    const res = await api.get('/api/booking/form-field-settings', { params });
    return res.data.data;
  },
  async saveFormFieldSettings(fields: { fieldCode: string; isVisible: boolean; isRequired: boolean; isListOnly?: boolean }[], organizationId?: string): Promise<void> {
    await api.put('/api/booking/form-field-settings', { fields, organizationId });
  },

  async getOrgResourceTypeCosts(organizationId?: string): Promise<{
    data: ResourceTypeCostRow[];
    topNodeOrganizationId: string;
  }> {
    const res = await api.get('/api/booking/resource-type-costs', {
      params: organizationId ? { organizationId } : {},
    });
    return { data: res.data.data, topNodeOrganizationId: res.data.topNodeOrganizationId };
  },
  async saveOrgResourceTypeCost(
    resourceTypeId: string,
    payload: ResourceTypeCostPayload,
    organizationId?: string
  ): Promise<ResourceTypeCostRow[]> {
    const res = await api.put(`/api/booking/resource-type-costs/${resourceTypeId}`, {
      ...payload,
      organizationId,
    });
    return res.data.data;
  },
  async getPickupResourceTypeCosts(pickupLocationId: string): Promise<{
    data: PickupResourceTypeCostOverride[];
    orgDefaults: ResourceTypeCostRow[];
    topNodeOrganizationId: string;
  }> {
    const res = await api.get(`/api/booking/pickup-locations/${pickupLocationId}/resource-type-costs`);
    return {
      data: res.data.data,
      orgDefaults: res.data.orgDefaults,
      topNodeOrganizationId: res.data.topNodeOrganizationId,
    };
  },
  async savePickupResourceTypeCost(
    pickupLocationId: string,
    resourceTypeId: string,
    payload: ResourceTypeCostPayload
  ): Promise<PickupResourceTypeCostOverride[]> {
    const res = await api.put(
      `/api/booking/pickup-locations/${pickupLocationId}/resource-type-costs/${resourceTypeId}`,
      payload
    );
    return res.data.data;
  },
  async getBookingProjects(params?: { listAll?: boolean; search?: string; organizationId?: string; pickupLocationOrgId?: string }): Promise<BookingProject[]> {
    const res = await api.get('/api/booking/projects', {
      params: {
        ...(params?.listAll ? { listAll: 'true' } : {}),
        ...(params?.search ? { search: params.search } : {}),
        ...(params?.organizationId ? { organizationId: params.organizationId } : {}),
        ...(params?.pickupLocationOrgId ? { pickupLocationOrgId: params.pickupLocationOrgId } : {}),
      },
    });
    return res.data.data;
  },
  async downloadProjectImportTemplate(): Promise<void> {
    const res = await api.get('/api/booking/projects/import-template', { responseType: 'blob' });
    const url = URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement('a');
    a.href = url;
    a.download = 'projekt-importmall.xlsx';
    a.click();
    URL.revokeObjectURL(url);
  },
  async importBookingProjects(file: File, organizationId?: string): Promise<ProjectImportResult> {
    const form = new FormData();
    form.append('file', file);
    if (organizationId) form.append('organizationId', organizationId);
    const res = await api.post('/api/booking/projects/import', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return res.data.data;
  },

  async getResourceTypes(): Promise<ResourceType[]> {
    const res = await api.get('/api/booking/resource-types');
    return res.data.data;
  },
  async getOrganizationResourceTypes(organizationId: string): Promise<string[]> {
    const res = await api.get(`/api/booking/organizations/${organizationId}/resource-types`);
    return res.data.data;
  },
  async setOrganizationResourceTypes(organizationId: string, resourceTypeIds: string[]): Promise<void> {
    await api.put(`/api/booking/organizations/${organizationId}/resource-types`, { resourceTypeIds });
  },

  async getOrgResourceTypes(organizationId: string, includeInactive = false): Promise<OrgResourceType[]> {
    const res = await api.get('/api/booking/org-resource-types', {
      params: { organizationId, includeInactive: includeInactive ? 'true' : undefined },
    });
    return res.data.data;
  },
  async createOrgResourceType(data: {
    organizationId?: string;
    code: string;
    name: string;
    nameEn?: string | null;
    displayOrder?: number;
  }): Promise<{ id: string }> {
    const res = await api.post('/api/booking/org-resource-types', data);
    return res.data.data;
  },
  async updateOrgResourceType(
    id: string,
    data: Partial<{ name: string; nameEn: string | null; displayOrder: number; isActive: boolean }>
  ): Promise<void> {
    await api.put(`/api/booking/org-resource-types/${id}`, data);
  },
  async deleteOrgResourceType(id: string): Promise<void> {
    await api.delete(`/api/booking/org-resource-types/${id}`);
  },

  async getBookingResources(
    organizationId: string,
    options?: { includeInactive?: boolean; unassignedOnly?: boolean }
  ): Promise<BookingResource[]> {
    const res = await api.get('/api/booking/resources', {
      params: {
        organizationId,
        includeInactive: options?.includeInactive ? 'true' : undefined,
        unassignedOnly: options?.unassignedOnly ? 'true' : undefined,
      },
    });
    return res.data.data;
  },
  async createBookingResource(data: {
    organizationId?: string;
    resourceTypeId: string;
    identity: string;
    name: string;
    description?: string | null;
  }): Promise<{ id: string }> {
    const res = await api.post('/api/booking/resources', data);
    return res.data.data;
  },
  async updateBookingResource(
    id: string,
    data: Partial<{
      resourceTypeId: string;
      identity: string;
      name: string;
      description: string | null;
      isActive: boolean;
    }>
  ): Promise<void> {
    await api.put(`/api/booking/resources/${id}`, data);
  },
  async deleteBookingResource(id: string): Promise<void> {
    await api.delete(`/api/booking/resources/${id}`);
  },
  async addResourceToPickupLocation(pickupLocationId: string, bookingResourceId: string): Promise<{ id: string }> {
    const res = await api.post(`/api/booking/pickup-locations/${pickupLocationId}/resources`, { bookingResourceId });
    return res.data.data;
  },

  async getOrganizationBookingPolicyHtml(organizationId: string): Promise<string | null> {
    const res = await api.get<{ success: boolean; data: { html: string | null } }>(
      `/api/booking/organizations/${encodeURIComponent(organizationId)}/booking-policy`
    );
    const html = res.data.data?.html;
    return html != null && String(html).trim() !== '' ? String(html) : null;
  },

  async getPickupLocationCabinets(pickupLocationId: string): Promise<PickupLocationCabinet[]> {
    const res = await api.get(`/api/booking/pickup-locations/${pickupLocationId}/cabinets`);
    return res.data.data;
  },
  async getAvailablePickupLocationCabinets(pickupLocationId: string): Promise<PickupLocationAvailableCabinet[]> {
    const res = await api.get(`/api/booking/pickup-locations/${pickupLocationId}/available-cabinets`);
    return res.data.data;
  },
  async assignPickupLocationCabinet(pickupLocationId: string, cabinetId: string): Promise<void> {
    await api.post(`/api/booking/pickup-locations/${pickupLocationId}/cabinets`, { cabinetId });
  },
  async unassignPickupLocationCabinet(pickupLocationId: string, cabinetId: string): Promise<void> {
    await api.delete(
      `/api/booking/pickup-locations/${pickupLocationId}/cabinets/${encodeURIComponent(cabinetId)}`
    );
  },

  async getPickupLocationVehicles(pickupLocationId: string): Promise<PickupLocationVehicle[]> {
    const res = await api.get(`/api/booking/pickup-locations/${pickupLocationId}/vehicles`);
    return res.data.data;
  },
  async getAvailableVehicles(
    pickupLocationId: string,
    startAt: string,
    endAt: string,
    excludeBookingId?: string,
  ): Promise<PickupLocationVehicle[]> {
    const res = await api.get(`/api/booking/pickup-locations/${pickupLocationId}/available-vehicles`, {
      params: { startAt, endAt, ...(excludeBookingId ? { excludeBookingId } : {}) },
    });
    return res.data.data;
  },
  async getAttestationUsers(pickupLocationId: string): Promise<{ id: string; email: string; firstName: string | null; lastName: string | null }[]> {
    const res = await api.get(`/api/booking/pickup-locations/${pickupLocationId}/attestation-users`);
    return res.data.data;
  },
  async addVehicleToPickupLocation(
    pickupLocationId: string,
    data:
      | { vehicleId: number; resourceTypeId?: string | null }
      | { supplierId: string; externalVehicleRef?: string; displayName?: string; resourceTypeId?: string | null }
  ): Promise<string> {
    const res = await api.post(`/api/booking/pickup-locations/${pickupLocationId}/vehicles`, data);
    return res.data.data.id;
  },
  async updatePickupLocationVehicle(
    id: string,
    data: Partial<{ displayName: string | null; isAvailable: boolean; resourceTypeId: string | null }>
  ): Promise<void> {
    await api.put(`/api/booking/pickup-location-vehicles/${id}`, data);
  },
  async removeVehicleFromPickupLocation(id: string): Promise<{ reassigned: number; collisions: number }> {
    const res = await api.delete(`/api/booking/pickup-location-vehicles/${id}`);
    return res.data?.data ?? { reassigned: 0, collisions: 0 };
  },

  async getBooking(id: string): Promise<Booking> {
    const res = await api.get(`/api/booking/bookings/${encodeURIComponent(id)}`);
    if (!res.data?.success) throw new Error(res.data?.error || 'getBooking failed');
    return res.data.data as Booking;
  },

  async getBookings(
    pickupLocationId: string,
    dateFrom: string,
    dateTo: string
  ): Promise<{ bookings: Booking[]; vehicleBlocks: VehicleBookingBlock[] }> {
    const res = await api.get('/api/booking/bookings', {
      params: { pickupLocationId, dateFrom, dateTo },
    });
    const d = res.data?.data;
    if (Array.isArray(d)) {
      return { bookings: d as Booking[], vehicleBlocks: [] };
    }
    return {
      bookings: (d?.bookings ?? []) as Booking[],
      vehicleBlocks: (d?.vehicleBlocks ?? []) as VehicleBookingBlock[],
    };
  },

  async getResourceDay(
    pickupLocationIds: string[],
    dateFrom: string,
    dateTo: string
  ): Promise<{
    vehicles: PickupLocationVehicle[];
    bookings: Booking[];
    vehicleBlocks: VehicleBookingBlock[];
  }> {
    const res = await api.get('/api/booking/resource-day', {
      params: { pickupLocationIds: pickupLocationIds.join(','), dateFrom, dateTo },
    });
    const d = res.data?.data;
    return {
      vehicles: (d?.vehicles ?? []) as PickupLocationVehicle[],
      bookings: (d?.bookings ?? []) as Booking[],
      vehicleBlocks: (d?.vehicleBlocks ?? []) as VehicleBookingBlock[],
    };
  },

  async getCollisionBookings(): Promise<CollisionBookingRow[]> {
    const res = await api.get('/api/booking/collision-bookings');
    return Array.isArray(res.data?.data) ? res.data.data : [];
  },

  async cancelCollisionBookingBySystem(id: string): Promise<void> {
    await api.post(`/api/booking/collision-bookings/${encodeURIComponent(id)}/cancel-by-system`);
  },

  async getBookingRequests(): Promise<BookingRequestRow[]> {
    const res = await api.get('/api/booking/booking-requests');
    return Array.isArray(res.data?.data) ? res.data.data : [];
  },

  async listVehicleBookingBlocks(vehicleId: number): Promise<VehicleBookingBlock[]> {
    const res = await api.get(`/api/booking/vehicles/${vehicleId}/booking-blocks`);
    return Array.isArray(res.data?.data) ? res.data.data : [];
  },

  async createVehicleBookingBlock(vehicleId: number, body: { startAt: string; endAt: string }): Promise<string> {
    const res = await api.post(`/api/booking/vehicles/${vehicleId}/booking-blocks`, body);
    return res.data?.data?.id ?? '';
  },

  async deleteVehicleBookingBlock(vehicleId: number, blockId: string): Promise<void> {
    await api.delete(`/api/booking/vehicles/${vehicleId}/booking-blocks/${encodeURIComponent(blockId)}`);
  },

  async listOrgVehicleBookingBlocks(): Promise<OrgVehicleBookingBlock[]> {
    const res = await api.get('/api/booking/vehicle-booking-blocks');
    return Array.isArray(res.data?.data) ? res.data.data : [];
  },

  async updateVehicleBookingBlock(
    vehicleId: number,
    blockId: string,
    body: { startAt: string; endAt: string }
  ): Promise<void> {
    await api.put(`/api/booking/vehicles/${vehicleId}/booking-blocks/${encodeURIComponent(blockId)}`, body);
  },

  async blockResourceFromBooking(
    bookingId: string,
    body: { hours: VehicleBlockHours; damageType: DamageTypeCode; description: string; location?: string }
  ): Promise<BlockResourceResult> {
    const res = await api.post(`/api/booking/bookings/${encodeURIComponent(bookingId)}/block-resource`, body);
    return res.data?.data as BlockResourceResult;
  },
  async getAllBookings(filters: BookingSearchFilters): Promise<BookingSearchRow[]> {
    const res = await api.get('/api/booking/all-bookings', { params: filters });
    const d = res.data?.data;
    if (!Array.isArray(d)) {
      throw new Error('Unexpected all-bookings response');
    }
    return d;
  },
  /** Kartposition för bokningens fordon. Kräver BOOKING_SEARCH + VIEW_VEHICLE_MAP. */
  async getBookingVehiclePosition(bookingId: string): Promise<BookingVehiclePosition> {
    const res = await api.get(`/api/booking/bookings/${encodeURIComponent(bookingId)}/vehicle-position`);
    return res.data.data as BookingVehiclePosition;
  },

  /** Tankkort (inkl. PIN) för bokningens fordon. Kräver BOOKING_CALENDAR + VIEW_FUEL_PIN. */
  async getBookingFuelCards(bookingId: string): Promise<BookingFuelCard[]> {
    const res = await api.get(`/api/booking/bookings/${encodeURIComponent(bookingId)}/fuel-cards`);
    const d = res.data?.data;
    return Array.isArray(d) ? d : [];
  },
  /** Nuläge (beläggning + nyckeltal) för en dag. Kräver BOOKING_OPTIMIZATION + VIEW. */
  async getDayUtilization(params: {
    date: string;
    pickupLocationId?: string;
    organizationId?: string;
  }): Promise<DayUtilizationResult> {
    const res = await api.get('/api/booking/optimization/day-utilization', { params });
    return res.data.data as DayUtilizationResult;
  },
  /** Kör AI-optimering för en dag. Kräver BOOKING_OPTIMIZATION + OPTIMIZE. */
  async optimizeDay(params: {
    date: string;
    pickupLocationId?: string;
    organizationId?: string;
  }): Promise<OptimizeDayResult> {
    const res = await api.post('/api/booking/optimization/optimize-day', params);
    return res.data.data as OptimizeDayResult;
  },
  /** Genomför dagsoptimeringen (flyttar bokningar) för en pool. Kräver BOOKING_OPTIMIZATION + OPTIMIZE. */
  async applyDayOptimization(params: {
    date: string;
    pickupLocationId: string;
  }): Promise<{ moves: (FitMove & { startAt: string; endAt: string })[]; beforeUsed: number; afterUsed: number; freed: number }> {
    const res = await api.post('/api/booking/optimization/apply-day', params);
    return res.data.data;
  },
  /** Kollar om en ny bokning ryms genom att flytta befintliga. Kräver BOOKING_OPTIMIZATION + OPTIMIZE. */
  async fitCheck(params: {
    date: string;
    startAt: string;
    endAt: string;
    pickupLocationId: string;
    requirements?: FitRequirements;
  }): Promise<FitPlan> {
    const res = await api.post('/api/booking/optimization/fit-check', params);
    return res.data.data as FitPlan;
  },
  /** Utför inpassningen: flyttar befintliga bokningar och skapar den nya. */
  async fitApply(params: {
    date: string;
    startAt: string;
    endAt: string;
    pickupLocationId: string;
    requirements?: FitRequirements;
    booking: Record<string, unknown>;
  }): Promise<FitApplyResult> {
    const res = await api.post('/api/booking/optimization/fit-apply', params);
    return res.data.data as FitApplyResult;
  },
  async createBooking(data: {
    pickupLocationVehicleId?: string;
    pickupLocationId?: string;
    supplierFallback?: boolean;
    requestBooking?: boolean;
    requestedCarClass?: string | null;
    startAt: string;
    endAt: string;
    customerName?: string | null;
    bookedByUserId?: string | null;
    notes?: string | null;
    bookingType?: BookingType;
    tripPurpose?: string | null;
    tripPurposeId?: number | null;
    purposeId?: string | null;
    passengerCount?: number;
    termsAcceptedAt?: string | null;
    termsVersion?: string | null;
    members?: { userId: string; canCheckout?: boolean }[];
    estimatedKm?: number | null;
    destinationId?: string | null;
    destinationText?: string | null;
    costCenterId?: string | null;
    costCenterText?: string | null;
    projectId?: number | null;
    projectText?: string | null;
    policyAcknowledged?: boolean;
    driverLicenseConfirmed?: boolean | null;
    approvedByUserId?: string | null;
    availableVehicleIdsAtBooking?: string[] | null;
    suggestedPickupLocationVehicleId?: string | null;
    recurrence?: { pattern: 'DAILY' | 'WEEKLY' | 'MONTHLY'; count: number };
    isServiceBooking?: boolean;
  }): Promise<string> {
    const res = await api.post('/api/booking/bookings', data);
    return res.data.data.id;
  },
  async updateBooking(
    id: string,
    data: Partial<{
      startAt: string;
      endAt: string;
      customerName: string | null;
      bookedByUserId: string | null;
      notes: string | null;
      bookingType: BookingType;
      tripPurpose: string | null;
      tripPurposeId: number | null;
      estimatedKm: number | null;
      destinationId: string | null;
      destinationText: string | null;
      costCenterId: string | null;
      costCenterText: string | null;
      projectId: number | null;
      projectText: string | null;
      driverLicenseConfirmed: boolean | null;
      approvedByUserId: string | null;
      availableVehicleIdsAtBooking: string[] | null;
      suggestedPickupLocationVehicleId: string | null;
      status: BookingStatus;
      pickupLocationVehicleId: string | null;
    }>
  ): Promise<void> {
    await api.put(`/api/booking/bookings/${id}`, data);
  },
  /**
   * Självbetjäning: ägare/medlem redigerar sin egen bokning (samma fält som Ny bokning).
   * Behörighet avgörs i backend (ägare/medlem/global admin), inte admin-EDIT.
   */
  async selfUpdateBooking(
    id: string,
    data: Partial<{
      startAt: string;
      endAt: string;
      pickupLocationVehicleId: string | null;
      customerName: string | null;
      bookedByUserId: string | null;
      notes: string | null;
      bookingType: BookingType;
      tripPurpose: string | null;
      tripPurposeId: number | null;
      estimatedKm: number | null;
      destinationId: string | null;
      destinationText: string | null;
      costCenterId: string | null;
      costCenterText: string | null;
      projectId: number | null;
      projectText: string | null;
      driverLicenseConfirmed: boolean | null;
      approvedByUserId: string | null;
      passengerCount: number;
      members: { userId: string; canCheckout?: boolean }[];
    }>,
  ): Promise<void> {
    await api.put(`/api/booking/bookings/${id}/self`, data);
  },
  /** Id:n på bokningar där inloggad användare är ägare eller medlem (för redigera-egen-bokning). */
  async getMyBookingIds(): Promise<Set<string>> {
    const res = await api.get('/api/booking/bookings/me');
    const rows = Array.isArray(res.data?.data) ? res.data.data : [];
    return new Set(rows.map((r: { id: string }) => String(r.id)));
  },
  async deleteBooking(id: string): Promise<void> {
    await api.delete(`/api/booking/bookings/${id}`);
  },
  /** Avboka (inaktivera) en bokning utan att radera den. */
  async cancelBooking(id: string): Promise<void> {
    await api.post(`/api/booking/bookings/${id}/cancel`);
  },
  async getBookingMaxExtend(id: string): Promise<string | null> {
    const res = await api.get(`/api/booking/bookings/${id}/max-extend`);
    const maxEndAt = res.data?.data?.maxEndAt;
    return typeof maxEndAt === 'string' && maxEndAt.trim() !== '' ? maxEndAt : null;
  },
  async selfCancelBooking(id: string): Promise<void> {
    await api.post(`/api/booking/bookings/${id}/self/cancel`);
  },
  /** Byt tilldelad resurs (fordon) på en bokning till ett annat i samma pool. */
  async changeBookingVehicle(id: string, pickupLocationVehicleId: string): Promise<void> {
    await api.put(`/api/booking/bookings/${id}/vehicle`, { pickupLocationVehicleId });
  },
  /** Flytta en bokning (tid och/eller fordon) till ett ledigt utrymme via tidslinjen. */
  async moveBooking(
    id: string,
    data: { startAt: string; endAt: string; pickupLocationVehicleId?: string }
  ): Promise<void> {
    await api.put(`/api/booking/bookings/${id}/move`, data);
  },
  /** Mjuk varning: bokningar där en användare redan har en överlappande bokning under perioden. */
  async getUserBookingConflicts(
    userId: string,
    startAt: string,
    endAt: string
  ): Promise<UserBookingConflict[]> {
    const res = await api.get('/api/booking/bookings/user-conflicts', {
      params: { userId, startAt, endAt },
    });
    const d = res.data?.data;
    return Array.isArray(d) ? d : [];
  },
  async sendCalendarInvite(bookingId: string, customerEmail: string): Promise<void> {
    await api.post(`/api/booking/bookings/${bookingId}/send-calendar-invite`, { customerEmail });
  },

  /** Publikt anrop (ingen auth) – hämta bokningsinfo för avbokningssida. */
  async getCancelInfoByToken(token: string): Promise<{
    valid: boolean;
    customerName: string | null;
    startAt: string;
    endAt: string;
    pickupLocationName: string;
    status?: string;
    canCancel?: boolean;
    poolBookingsLocked?: boolean;
  } | null> {
    const res = await api.get(`/api/booking/cancel-by-token/${token}`);
    if (!res.data.success || !res.data.valid) return null;
    return {
      valid: true,
      customerName: res.data.customerName ?? null,
      startAt: res.data.startAt,
      endAt: res.data.endAt,
      pickupLocationName: res.data.pickupLocationName,
      status: res.data.status,
      canCancel: res.data.canCancel !== false,
      poolBookingsLocked: res.data.poolBookingsLocked === true,
    };
  },
  /** Publikt anrop (ingen auth) – avboka via token. */
  async cancelByToken(token: string): Promise<void> {
    await api.post('/api/booking/cancel-by-token', { token });
  },

  // --- Booking Purposes ---

  async getBookingPurposes(organizationId?: string): Promise<BookingPurpose[]> {
    const params = organizationId ? { organizationId } : {};
    const res = await api.get('/api/booking/purposes', { params });
    return res.data.data;
  },
  async createBookingPurpose(data: { name: string; displayOrder?: number; organizationId?: string }): Promise<{ id: string }> {
    const res = await api.post('/api/booking/purposes', data);
    return res.data.data;
  },
  async updateBookingPurpose(id: string, data: { name?: string; isActive?: boolean; displayOrder?: number }): Promise<void> {
    await api.put(`/api/booking/purposes/${id}`, data);
  },

  // --- Syften (Syfte med resan) ---

  async getBookingTripPurposes(params?: { organizationId?: string; listAll?: boolean }): Promise<BookingTripPurpose[]> {
    const res = await api.get('/api/booking/trip-purposes', {
      params: {
        ...(params?.organizationId ? { organizationId: params.organizationId } : {}),
        ...(params?.listAll ? { listAll: 'true' } : {}),
      },
    });
    return res.data.data;
  },
  async createBookingTripPurpose(data: { name: string; displayOrder?: number; organizationId?: string }): Promise<{ id: number }> {
    const res = await api.post('/api/booking/trip-purposes', data);
    return res.data.data;
  },
  async updateBookingTripPurpose(id: number, data: { name?: string; isActive?: boolean; displayOrder?: number }): Promise<void> {
    await api.put(`/api/booking/trip-purposes/${id}`, data);
  },

  // --- Booking Terms ---

  async getActiveBookingTerms(organizationId?: string): Promise<BookingTerms | null> {
    const params = new URLSearchParams();
    if (organizationId) params.set('organizationId', organizationId);
    const query = params.toString();
    const res = await api.get(`/api/booking/terms${query ? `?${query}` : ''}`);
    return res.data.data ?? null;
  },
  async getAllBookingTerms(organizationId?: string): Promise<BookingTerms[]> {
    const params = new URLSearchParams();
    if (organizationId) params.set('organizationId', organizationId);
    const query = params.toString();
    const res = await api.get(`/api/booking/terms/all${query ? `?${query}` : ''}`);
    const d = res.data?.data;
    return Array.isArray(d) ? d : [];
  },
  async createBookingTerms(data: {
    version: string;
    title: string;
    content: string;
    organizationId?: string;
  }): Promise<{ id: string }> {
    const res = await api.post('/api/booking/terms', data);
    return res.data.data;
  },
  async updateBookingTerms(id: string, data: { title?: string; content?: string; isActive?: boolean }): Promise<void> {
    await api.put(`/api/booking/terms/${id}`, data);
  },

  // --- Booking Members ---

  async getBookingMembers(bookingId: string): Promise<BookingMember[]> {
    const res = await api.get(`/api/booking/bookings/${bookingId}/members`);
    return res.data.data;
  },
  async addBookingMember(bookingId: string, data: { userId: string; canCheckout?: boolean }): Promise<{ id: string }> {
    const res = await api.post(`/api/booking/bookings/${bookingId}/members`, data);
    return res.data.data;
  },
  async removeBookingMember(bookingId: string, userId: string): Promise<void> {
    await api.delete(`/api/booking/bookings/${bookingId}/members/${userId}`);
  },

  // --- Equipment Tags ---
  async getEquipmentTags(options?: {
    includeInactive?: boolean;
    organizationId?: string;
  }): Promise<EquipmentTag[]> {
    const params = new URLSearchParams();
    if (options?.includeInactive) params.set('includeInactive', '1');
    if (options?.organizationId) params.set('organizationId', options.organizationId);
    const query = params.toString();
    const res = await api.get(`/api/booking/equipment-tags${query ? `?${query}` : ''}`);
    return res.data.data;
  },
  async createEquipmentTag(data: {
    name: string;
    icon?: string;
    displayOrder?: number;
    organizationId?: string;
  }): Promise<{ id: string }> {
    const res = await api.post('/api/booking/equipment-tags', data);
    return res.data.data;
  },
  async updateEquipmentTag(id: string, data: { name?: string; icon?: string; isActive?: boolean; displayOrder?: number }): Promise<void> {
    await api.put(`/api/booking/equipment-tags/${id}`, data);
  },

  // --- Vehicle Equipment ---
  async getVehicleEquipment(vehicleId: number): Promise<VehicleEquipmentItem[]> {
    const res = await api.get(`/api/booking/vehicles/${vehicleId}/equipment`);
    return res.data.data;
  },
  async addVehicleEquipment(vehicleId: number, data: { equipmentTagId: string; notes?: string }): Promise<{ id: string }> {
    const res = await api.post(`/api/booking/vehicles/${vehicleId}/equipment`, data);
    return res.data.data;
  },
  async removeVehicleEquipment(vehicleId: number, tagId: string): Promise<void> {
    await api.delete(`/api/booking/vehicles/${vehicleId}/equipment/${tagId}`);
  },

  // --- Checkout / Return ---

  async checkoutBooking(bookingId: string, data: { source: string; timestamp?: string; userId?: string }): Promise<void> {
    await api.post(`/api/booking/bookings/${bookingId}/checkout`, data);
  },
  async returnBooking(bookingId: string, data: { source: string; timestamp?: string; userId?: string }): Promise<void> {
    await api.post(`/api/booking/bookings/${bookingId}/return`, data);
  },

  /** Kundtjänst: tillåt återlämning utan nyckel/kort för en specifik bokning (kräver orsak). */
  async grantReturnOverride(bookingId: string, reason: string): Promise<void> {
    await api.post(`/api/booking/bookings/${bookingId}/return-override`, { reason });
  },

  /** Kundtjänst: tillåt återlämning när GPS inte visar bilen på plats. */
  async grantReturnOffsite(bookingId: string, reason: string): Promise<void> {
    await api.post(`/api/booking/bookings/${bookingId}/return-offsite`, { reason });
  },

  /**
   * Kundtjänst: ge 15 min åtkomst igen efter återlämning (glömd väska).
   * Måste ske inom 60 min från returned_at. Ingen ledighetskontroll.
   */
  async grantPostReturnAccess(bookingId: string): Promise<{
    bookingId: string;
    status: 'CHECKED_OUT';
    endAt: string;
  }> {
    const res = await api.post(`/api/booking/bookings/${bookingId}/post-return-access`);
    return res.data.data;
  },

  // --- Vehicle Pools ---

  async getPoolAssignments(): Promise<VehiclePoolAssignment[]> {
    const res = await api.get('/api/booking/pools');
    return res.data.data;
  },
  async getPoolCandidateKeys(params: {
    organizationId: string;
    pickupLocationId?: string;
    cabinetId?: string;
    includePickupLocationId?: string;
  }): Promise<VehiclePoolCandidateKey[]> {
    const res = await api.get('/api/booking/pools/candidate-keys', { params });
    return res.data.data;
  },
  async createPoolAssignment(data: {
    organizationId?: string;
    pickupLocationId: string;
    name: string;
    maxHoursPerCheckout?: number;
    selectedKeyIds?: string[];
    memberUserIds?: string[];
  }): Promise<{ id: string }> {
    const res = await api.post('/api/booking/pools', data);
    return res.data.data;
  },
  async updatePoolAssignment(id: string, data: { name?: string; maxHoursPerCheckout?: number; isActive?: boolean; selectedKeyIds?: string[] }): Promise<void> {
    await api.put(`/api/booking/pools/${id}`, data);
  },
  async deletePoolAssignment(id: string): Promise<void> {
    await api.delete(`/api/booking/pools/${id}`);
  },
  async getPoolMembers(poolId: string): Promise<VehiclePoolMember[]> {
    const res = await api.get(`/api/booking/pools/${poolId}/members`);
    return res.data.data;
  },
  async addPoolMember(poolId: string, data: { userId: string; validFrom?: string; validTo?: string }): Promise<{ id: string }> {
    const res = await api.post(`/api/booking/pools/${poolId}/members`, data);
    return res.data.data;
  },
  async removePoolMember(poolId: string, userId: string): Promise<void> {
    await api.delete(`/api/booking/pools/${poolId}/members/${userId}`);
  },

  async getAssignableFleetVehiclesForPool(pickupLocationId: string): Promise<FleetVehicleOption[]> {
    const res = await api.get(`/api/booking/pickup-locations/${pickupLocationId}/assignable-fleet-vehicles`);
    return res.data.data;
  },
  async assignPermanentFromFleet(pickupLocationId: string, vehicleId: number): Promise<string> {
    const res = await api.post(`/api/booking/pickup-locations/${pickupLocationId}/assign-permanent-fleet`, { vehicleId });
    return res.data.data.id;
  },
  async getAssignableFleetVehiclesForBooking(bookingId: string): Promise<FleetVehicleOption[]> {
    const res = await api.get(`/api/booking/booking-requests/${bookingId}/assignable-fleet-vehicles`);
    return res.data.data;
  },
  async assignBookingFromFleet(bookingId: string, vehicleId: number): Promise<void> {
    await api.post(`/api/booking/booking-requests/${bookingId}/assign-from-fleet`, { vehicleId });
  },
  async listBookingBasis(params: {
    q?: string;
    dateFrom?: string;
    dateTo?: string;
    vehicleId?: number;
    status?: string;
  }): Promise<BookingBasisListItem[]> {
    const res = await api.get('/api/booking/booking-basis', { params });
    return res.data.data;
  },
  async getBookingBasis(bookingId: string): Promise<BookingBasisPayload> {
    const res = await api.get(`/api/booking/booking-basis/${bookingId}`);
    return res.data.data;
  },
  async downloadBookingBasisPdf(bookingId: string, type: 'summary' = 'summary'): Promise<void> {
    const response = await api.get(`/api/booking/booking-basis/${bookingId}/pdf`, {
      params: { type },
      responseType: 'blob',
    });
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    const contentDisposition = response.headers['content-disposition'] as string | undefined;
    let filename = 'bokningsunderlag.pdf';
    if (contentDisposition) {
      const match = contentDisposition.match(/filename="([^"]+)"|filename=([^\s;]+)/);
      if (match) filename = (match[1] || match[2]).replace(/['"]/g, '');
    }
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  },
};
